import { useEmployeeStore } from '@/stores/useEmployeeStore';

export default {
    data() {
        return {
            fieldsWithErrors: [],
            showErrors: true,
            validationResults: {},
        };
    },
    methods: {
        validatePositiveInteger(name) {
            const { value = '' } = this.formData[name];
            if (!value || typeof value !== 'string') return true;

            // REGEX: Allows only digits => can only accept positive integers
            // Also, prevents number prefix/postfix's
            const re = /^\d+$/;
            if (!value.match(re)) return true;

            const int = parseInt(value, 10);
            return Number.isNaN(int);
        },
        validateStringTooShort(threshold) {
            return (name) => {
                const { value = '' } = this.formData[name];
                const wordCount = value.trim().split(/\s+/).filter(Boolean).length;

                return wordCount <= threshold;
            };
        },
        validateStringTooLong(threshold) {
            return (name) => {
                const { value = '' } = this.formData[name];
                const wordCount = value.trim().split(/\s+/).filter(Boolean).length;

                return wordCount > threshold;
            };
        },
        validateAll() {
            const fields = Object.keys(this.formData);
            fields.forEach((field) => {
                this.validateInput(field);
            });

            return fields.filter((each) => this.formData[each].error);
        },
        isValid(key) {
            const { value, required } = this.formData[key];

            const noValue = !value || value.length < 1;

            if (required && noValue) {
                const errorString = this.formData[key].requiredError || 'This field is required';
                this.formData[key].errorString = errorString;
                return false;
            }

            if (this.formData[key].validation) {
                const validationObj = this.formData[key].validation;
                const checks = Object.keys(validationObj);

                const validationFailures = checks
                    .filter((check) => {
                        const { function: valFunc, errorString } = validationObj[check];
                        const validFunctionExists = Boolean(valFunc);

                        return validFunctionExists && errorString;
                    })
                    .find((check) => validationObj[check].function(key));

                const validationWarnings = checks
                    .filter((check) => {
                        const { function: valFunc, warningString } = validationObj[check];
                        const validFunctionExists = Boolean(valFunc);

                        return validFunctionExists && warningString;
                    })
                    .find((check) => validationObj[check].function(key));

                if (validationWarnings) {
                    this.formData[key].warningString = validationObj[validationWarnings].warningString;
                } else this.formData[key].warningString = null;

                if (validationFailures) {
                    this.formData[key].errorString = validationObj[validationFailures].errorString;
                    return false;
                }
            }

            return true;
        },
        validateInput(key) {
            const passed = this.isValid(key);
            const formEntry = this.formData[key];

            // 'status' is for the purpose of display state only
            // only update the status for an individual field if errors should be visible
            if (this.showErrors) {
                formEntry.status = null;
                if (formEntry.warningString) formEntry.status = 'warning';
                if (!passed) formEntry.status = 'error';
            }

            if (passed && this.fieldsWithErrors.includes(key)) {
                const match = this.fieldsWithErrors.findIndex((each) => each === key);
                this.fieldsWithErrors.splice(match, 1);
            }

            formEntry.error = !passed;
        },
        async handleSaveAndContinue(resultsOnly) {
            this.fieldsWithErrors = this.validateAll();
            if (this.fieldsWithErrors.length > 0) {
                this.showErrors = true;
                this.fieldsWithErrors.forEach((field) => {
                    this.formData[field].status = 'error';
                });
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                const excludedFields = ['privacyAndTermsChecked'];
                const storeable = {};

                const fields = Object.keys(this.formData).filter((field) => !excludedFields.includes(field));
                fields.forEach((field) => {
                    storeable[field] = this.formData[field].value;
                });

                // option to just get the storable fields
                if (resultsOnly) {
                    this.validationResults = storeable;
                    return;
                }

                // run any save action if validation passes
                // this should be set on the component
                if (this.saveAction) {
                    this.saveAction(storeable);
                }
            }
        },
        overlapFields(data1, data2) {
            // get all fields that exist in both of two objects
            const dataFields = Object.keys(data1).map((key) => key.toLowerCase());
            return Object.keys(data2).filter((name) => dataFields.includes(name.toLowerCase()));
        },
        async populateFields(enumFields, loadedData) {
            const store = useEmployeeStore();
            const data = loadedData || store.user.data;

            const formFields = this.overlapFields(data, this.formData);
            const inputFields = formFields.filter((each) => !enumFields.includes(each));
            // handle fields that are stored as numbers but used by text inputs
            const numberFields = ['classSize'];

            // enum/select fields that need some standardization
            enumFields.forEach((each) => {
                const incomingValue = data[each];
                this.formData[each].value = incomingValue ? incomingValue.toLowerCase() : '';
            });

            // keep case of input fields, may be descriptions, titles, or longer text
            inputFields.forEach((each) => {
                if (numberFields.includes(each)) {
                    this.formData[each].value = data[each].toString();
                } else {
                    this.formData[each].value = data[each];
                }
            });

            return this.validateAll();
        },
    },
};
